// The trace-review pipeline's reviews WITH FINDINGS for a scope, listed newest
// first, and an opened session's findings with what they quoted: the Reviews
// lens's read.
//
// A review is one report. The pipeline judges a conversation window by window
// and writes one report per window, carrying the judge's own account of what
// the agent did there (the summary) beside the findings it raised. The lanes
// read the reports of ONE conversation through the review API
// (traceReviewReports.js); this module reads them for MANY conversations at
// once through the analytics graph, where the report header is projected
// (CONVERSATION_REVIEW_REPORT) with its prose beside it while the document is
// retained (CONVERSATION_REVIEW_REPORT_TEXT, a LEFT join so an expired
// document keeps its header row). Only reports that raised something are
// listed: a clean report is the pipeline's normal answer, and a list that is
// mostly "clean" buries the rows a reader came for.
//
// Scope is resolved by SESSION. The explore scope is a prompt-side filter --
// endpoint, agent, topic, tool, skill, whatever the sidebar can drill -- that
// the report table cannot carry, so the lens first lists the sessions in scope
// (getSessionLog, the read behind the overview's Sessions log) and then reads
// the reports whose trace is one of them. The tenant root has no filter and
// reads the reports directly, so at the root the list is every flagged review
// completed in the range. Both reads are bounded -- the newest
// REVIEW_SESSION_CAP sessions, the newest reports per page -- and each says
// when its bound was reached, because a list that is silently a sample reads
// as the whole: the summary line when the session read was capped, a
// session's own row when its older reviews did not load.
//
// An opened session reads its reviews' findings once, in one bounded read:
// each finding's rungs, title and rationale, and the passages it quoted
// paired with the messages they came from. Where those messages sit on the
// session's turns is triageTimeline's read, so the lens numbers them the way
// the lanes do.
import { runQueryRows, rangeToTimeRange, rangeBoundsMs } from "./analyticsClient.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { conductBySession, CONDUCT_FINDINGS_LIMIT } from "./conductTypes.js";
import { reportTone } from "./traceReviewReports.js";
import { getSessionPromptPreviews } from "./sessionLabels.js";
import { citationsOf } from "./findingCitations.js";

// Reports read per scope. Past this the list says "newest N shown" rather
// than paging: two hundred flagged reviews is already more than a reader scans.
export const REVIEWS_LIMIT = 200;
// Sessions the scoped read resolves before asking for their reports. The
// analytics planner refuses an IN list past 1,024 values; this stays well
// under it and matches the sessions the overview lists before paging.
export const REVIEW_SESSION_CAP = 500;
// Finding rows read for an opened session's reviews. The entity is
// record-bounded at 1,000 rows per query, so the read cannot ask one past its
// ceiling: a full page is reported as possibly truncated instead. A flagged
// review carries a few findings, so the bound is well above one session's
// own. The planner refuses an IN list past 1,024 values.
export const REVIEW_FINDINGS_LIMIT = 1000;
export const CITED_MESSAGE_CAP = 1024;
// A review completes after the activity it judged, so the completion window
// runs a little past the range; the graph refuses windows over 90 days.
const WINDOW_TAIL_MS = 3600 * 1000;
const MAX_WINDOW_MS = 90 * 86400 * 1000;
// The planner names output columns from a fixed pool of 32 aliases and fails
// the whole query past it; the test pins every projection against this.
export const REVIEWS_OUTPUT_ALIAS_CAP = 32;

const R = (name) => ({ stepAlias: "r", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_REPORT_${name}` });
// The findings a report counts here: the ones the pipeline kept (actionable).
// A report's `finding_count` also holds the ones it withheld or dropped as
// context-only or unsupported; the finding table, the conduct pool and the
// triage map hold none of those, so a session listed for them alone would
// open to nothing.
const KEPT_FINDINGS = "ACTIONABLE_FINDING_COUNT";
const T = (name) => ({ stepAlias: "t", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_REPORT_TEXT_${name}` });
const F = (name) => ({ stepAlias: "f", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_${name}` });
const pred = (field, op, value) => ({ pred: { field, op: `OPERATOR_${op}`, value } });
const and = (children) => (children.length === 1 ? children[0] : { andExpr: { children } });
const timeRange = (fromMs, toMs) => rangeToTimeRange({ from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() });

// Header columns the list reads. Selection revision and target set hash are
// here so a re-evaluation of the same window supersedes the report it replaced
// instead of appearing beside it; the target count is the strip's axis.
const HEADER_FIELDS = [
    ["REPORT_ID", "report_id"], ["TRACE_ID", "trace_id"], ["ENDPOINT_ID", "endpoint_id"],
    ["COMPLETED_AT", "completed_at"], ["DOCUMENT_EXPIRES_AT", "document_expires_at"],
    ["TARGET_SET_HASH", "target_set_hash"], ["SELECTION_REVISION", "selection_revision"],
    ["CLEAN", "clean"], ["IS_PARTIAL", "is_partial"],
    ["FINDING_COUNT", "finding_count"], ["ACTIONABLE_FINDING_COUNT", "actionable_finding_count"],
    ["WITHHELD_FINDING_COUNT", "withheld_finding_count"],
    ["WORST_BEHAVIOUR_SEVERITY", "worst_behaviour_severity"], ["WORST_CONTENT_SENSITIVITY", "worst_content_sensitivity"],
    ["PROVIDER_MODEL_IDENTIFIER", "model"], ["TARGET_ITEM_COUNT", "target_item_count"],
];
// Prose columns: sensitive, so read only for a role that may see conversation
// content. Without them the row still says what it found, not what it read.
const TEXT_FIELDS = [["SUMMARY", "summary"]];

// The completion window for a range: the range itself, run a little past its
// end for reviews still completing, and clipped to the graph's 90-day ceiling
// from that end.
export function reviewsWindow(range, nowMs = Date.now()) {
    const { fromMs, toMs } = rangeBoundsMs(range);
    const to = Math.min(Math.max(toMs, fromMs) + WINDOW_TAIL_MS, nowMs + WINDOW_TAIL_MS);
    return { fromMs: Math.max(fromMs, to - MAX_WINDOW_MS), toMs: to };
}

const uniqueIds = (ids, cap) => [...new Set((ids || []).filter(Boolean).map((id) => String(id).toLowerCase()))].slice(0, cap);

// `sessionIds` null reads the whole tenant (the root scope); an array reads
// the reports of those sessions, newest REVIEW_SESSION_CAP of them. Only
// reports that raised a finding are asked for.
export function reviewsQuerySpec({ sessionIds = null, fromMs, toMs, limit = REVIEWS_LIMIT, sensitive = true }) {
    const root = { alias: "r", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_REPORT" };
    if (sensitive) {
        root.children = [{
            edge: "EDGE_TYPE_CONVERSATION_REVIEW_REPORT_HAS_TEXT",
            mode: "JOIN_MODE_LEFT",
            target: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_REPORT_TEXT" },
        }];
    }
    const ids = Array.isArray(sessionIds) ? uniqueIds(sessionIds, REVIEW_SESSION_CAP) : null;
    const preds = [pred(R(KEPT_FINDINGS), "GT", 0)];
    if (ids) preds.unshift(pred(R("TRACE_ID"), "IN", ids));
    return {
        root,
        timeRange: timeRange(fromMs, toMs),
        filter: and(preds),
        output: {
            dimensions: [
                ...HEADER_FIELDS.map(([name, alias]) => ({ field: R(name), outputAlias: alias })),
                ...(sensitive ? TEXT_FIELDS.map(([name, alias]) => ({ field: T(name), outputAlias: alias })) : []),
            ],
        },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_DESC" }],
        // One past the ceiling, so "there were more" is a fact and not a guess.
        limit: limit + 1,
    };
}

// Harness and test seams: pre-seeded answers render through the fetch paths
// with no backend. Cleared together.
let SEEDED = null;
let SEEDED_SESSIONS = null;
let SEEDED_TOTALS = null;
let SEEDED_FLOW = null;
let SEEDED_SESSION_FINDINGS = null;
let SEEDED_DROPPED = null;
let SEEDED_FINDINGS = null;
export function seedScopeReviews(rows, { truncated = false } = {}) {
    SEEDED = { rows: Array.isArray(rows) ? rows : [], truncated };
}
// The seeded page answers every offset; a seeded total decides whether the
// list offers more.
export function seedScopeReviewSessions({ aggregates = [], reports = [], previews = null, reportsTruncated = false } = {}) {
    SEEDED_SESSIONS = { aggregates, reports, previews: previews instanceof Map ? previews : new Map(Object.entries(previews || {})), reportsTruncated };
}
export function seedScopeReviewTotals({ sessions = 0, reviews = 0 } = {}) {
    SEEDED_TOTALS = { sessions, reviews };
}
export function seedReviewFlow(sums) {
    SEEDED_FLOW = sums && typeof sums === "object" ? { ...sums } : null;
}
// Finding rows as the opened-session read returns them; each session is
// answered with the rows of its own reviews.
export function seedSessionFindings(rows) {
    SEEDED_SESSION_FINDINGS = Array.isArray(rows) ? rows : [];
}
// What the opened-session reads of the findings the reviews did not keep
// return: the session's report headers, kept findings or not, and the
// finding-detail and citation rows.
export function seedSessionDroppedFindings({ reports = [], details = [], citations = [] } = {}) {
    SEEDED_DROPPED = {
        reports: Array.isArray(reports) ? reports : [],
        details: Array.isArray(details) ? details : [],
        citations: Array.isArray(citations) ? citations : [],
    };
}
export function seedScopeReviewFindings(rows, { clipped = false } = {}) {
    SEEDED_FINDINGS = { rows: Array.isArray(rows) ? rows : [], clipped };
}
export function clearScopeReviewsCache() {
    SEEDED = null;
    SEEDED_SESSIONS = null;
    SEEDED_TOTALS = null;
    SEEDED_FLOW = null;
    SEEDED_SESSION_FINDINGS = null;
    SEEDED_DROPPED = null;
    SEEDED_FINDINGS = null;
}

// -- conduct ------------------------------------------------------------------
// The scope's findings, enough of each to tell one habit from another: what
// the judge called it, how bad, and the words it cited. Newest first. Prose
// and quotes are sensitive, so the read exists only for a role that may see
// them. The pool grows in steps of CONDUCT_FINDINGS_LIMIT on request and
// stops at the ceiling; past it the reader narrows the scope or the range.
export const CONDUCT_FINDINGS_CEILING = 8000;
const CONDUCT_FIELDS = [
    ["TRACE_ID", "trace_id"], ["ENDPOINT_ID", "endpoint_id"], ["ID", "finding_id"], ["TITLE", "title"],
    ["BEHAVIOUR_CATEGORY", "behaviour_category"], ["BEHAVIOUR_SEVERITY", "behaviour_severity"],
    ["CONTENT_SENSITIVITY", "content_sensitivity"], ["CITATION_QUOTES", "citation_quotes"], ["CITATION_MESSAGE_IDS", "citation_message_ids"], ["COMPLETED_AT", "completed_at"],
];
// The gateway bounds one finding read at this many rows.
export const CONDUCT_PAGE = 1000;
export function reviewFindingsSpec({ sessionIds = null, fromMs, toMs, limit = CONDUCT_PAGE, offset = 0 }) {
    const ids = Array.isArray(sessionIds) ? uniqueIds(sessionIds, REVIEW_SESSION_CAP) : null;
    const spec = {
        root: { alias: "f", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING" },
        timeRange: timeRange(fromMs, toMs),
        output: { dimensions: CONDUCT_FIELDS.map(([name, alias]) => ({ field: F(name), outputAlias: alias })) },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_DESC" }],
        limit,
        ...(offset > 0 ? { offset } : {}),
    };
    if (ids) spec.filter = pred(F("TRACE_ID"), "IN", ids);
    return spec;
}
// -> { rows, clipped }. One step of the pool, starting at `offset`: the read
// comes in pages at the gateway's bound, the first alone, and only when it
// came back full the rest together. Clipped means every page was full, so
// the scope may hold more past this step. Pages over a table still being
// written can overlap; a finding counts once. A scope with no sessions has
// no findings and asks nothing.
export async function fetchScopeReviewFindings({ sessionIds = null, fromMs, toMs, limit = CONDUCT_FINDINGS_LIMIT, page = CONDUCT_PAGE, offset = 0, signal = null } = {}) {
    if (SEEDED_FINDINGS) return offset > 0 ? { rows: [], clipped: false } : SEEDED_FINDINGS;
    if (Array.isArray(sessionIds) && sessionIds.length === 0) return { rows: [], clipped: false };
    const size = Math.max(1, Math.min(page, limit));
    const pages = Math.max(1, Math.ceil(limit / size));
    const read = (at) => runQueryRows(reviewFindingsSpec({ sessionIds, fromMs, toMs, limit: size, offset: offset + at }), { signal });
    const first = (await read(0)) || [];
    const rest = first.length >= size && pages > 1
        ? (await Promise.all(Array.from({ length: pages - 1 }, (_, i) => read(size * (i + 1))))).map((p) => p || [])
        : [];
    const all = [first, ...rest];
    const seen = new Set();
    const rows = [];
    for (const r of all.flat()) {
        const id = String(r?.finding_id ?? "");
        if (!id || seen.has(id)) continue;
        seen.add(id);
        rows.push(r);
    }
    return { rows: rows.slice(0, limit), clipped: all.every((p) => p.length >= size) };
}
// The scope's conduct, as the session rows and the conduct list read it.
export function buildConduct(findings) {
    if (!findings?.rows?.length) return null;
    return { ...conductBySession(findings.rows), clipped: !!findings.clipped };
}

// The conduct list's own search and order. The search reads a type's name
// and every wording the judge used for it.
export function filterConduct(types = [], search = "") {
    const needle = String(search || "").trim().toLowerCase();
    if (!needle) return types;
    return types.filter((t) => t.name.toLowerCase().includes(needle)
        || t.incidents.some((i) => i.titles.some((x) => x.toLowerCase().includes(needle))));
}
export const CONDUCT_SORT_OPTIONS = [
    ["sessions", "Most sessions"],
    ["findings", "Most findings"],
    ["severity", "Worst first"],
    ["latest", "Latest"],
];
export function sortConduct(types = [], sort = "sessions") {
    const bySessions = (a, b) => (b.sessions.size - a.sessions.size) || (b.findings - a.findings) || a.index - b.index;
    const rows = [...types];
    if (sort === "findings") return rows.sort((a, b) => (b.findings - a.findings) || bySessions(a, b));
    if (sort === "severity") return rows.sort((a, b) => (rankIn(severityOrder, b.worst) - rankIn(severityOrder, a.worst)) || bySessions(a, b));
    if (sort === "latest") return rows.sort((a, b) => String(b.latestAt).localeCompare(String(a.latestAt)) || bySessions(a, b));
    return rows.sort(bySessions);
}

// -> { rows, truncated }. A scope with no sessions has no reviews and asks
// nothing. Throws on a failed read (denied, unreachable, or an analytics
// surface that predates the entity); the caller decides how to say so.
export async function fetchScopeReviews({ sessionIds = null, fromMs, toMs, sensitive = true, limit = REVIEWS_LIMIT, signal = null } = {}) {
    if (SEEDED) return SEEDED;
    if (Array.isArray(sessionIds) && sessionIds.length === 0) return { rows: [], truncated: false };
    const rows = await runQueryRows(reviewsQuerySpec({ sessionIds, fromMs, toMs, limit, sensitive }), { signal });
    return { rows: rows.slice(0, limit), truncated: rows.length > limit };
}

// -- shaping ----------------------------------------------------------------

const str = (v) => (v == null ? "" : String(v)).trim();
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const bool = (v) => v === true || v === 1 || v === "1" || str(v).toLowerCase() === "true";
// The projection stores the rung bare and lowercase ("high"); the review API
// spells it as the enum ("BEHAVIOUR_SEVERITY_HIGH"). Both read the same here,
// and the unspecified and none rungs read as no rung, which the tone helpers
// treat as calm.
function rung(value, prefix) {
    const s = str(value).toUpperCase();
    const bare = (s.startsWith(prefix) ? s.slice(prefix.length) : s).toLowerCase();
    return !bare || bare === "unspecified" || bare === "none" ? null : bare;
}

// One report row as the lens holds it: the same names the lanes' report
// timeline uses (traceReviewReports.adaptReport), so reportTone and
// reportLabel read either.
export function adaptReviewRow(row, nowMs = Date.now()) {
    const completedAt = parseServerTimestamp(row?.completed_at);
    const documentExpiresAt = parseServerTimestamp(row?.document_expires_at);
    return {
        id: str(row?.report_id),
        sessionId: str(row?.trace_id).toLowerCase(),
        endpointId: str(row?.endpoint_id) || null,
        completedAt: Number.isFinite(completedAt) ? completedAt : null,
        targetSetHash: str(row?.target_set_hash),
        selectionRevision: num(row?.selection_revision),
        clean: bool(row?.clean),
        partial: bool(row?.is_partial),
        // The findings the lens counts are the ones the pipeline kept; the
        // report's own count also holds the ones it withheld, or dropped as
        // context-only or unsupported, which no finding read returns.
        findingCount: num(row?.actionable_finding_count),
        writtenFindingCount: num(row?.finding_count),
        withheldFindingCount: num(row?.withheld_finding_count),
        worstBehaviour: rung(row?.worst_behaviour_severity, "BEHAVIOUR_SEVERITY_"),
        worstContent: rung(row?.worst_content_sensitivity, "CONTENT_SENSITIVITY_"),
        model: str(row?.model),
        targetItemCount: num(row?.target_item_count),
        summary: str(row?.summary),
        // The prose column is read through a LEFT join that yields nothing once
        // the document expires, so an empty summary is "expired" only when the
        // header says the document's time is up; otherwise the judge wrote none.
        expired: Number.isFinite(documentExpiresAt) && documentExpiresAt <= nowMs,
    };
}

// A later publication over the same window supersedes an earlier one: higher
// selection revision wins, newest completion breaking ties.
function supersedes(a, b) {
    if (a.selectionRevision !== b.selectionRevision) return a.selectionRevision > b.selectionRevision;
    return (a.completedAt ?? 0) > (b.completedAt ?? 0);
}

// Rows -> { reviews, superseded, truncated, completedAt }. Reviews are newest
// first, each carrying its tone and, when the scoped read listed it, the
// session row it judged, so a click can seed the trace tab with it.
export function buildReviewsList(rows = [], { truncated = false, sessionsById = null, nowMs = Date.now() } = {}) {
    const adapted = (rows || []).map((row) => adaptReviewRow(row, nowMs)).filter((r) => r.id);
    const byWindow = new Map();
    for (const r of adapted) {
        const key = r.targetSetHash ? `${r.sessionId}:${r.targetSetHash}` : `id:${r.id}`;
        const cur = byWindow.get(key);
        if (!cur || supersedes(r, cur)) byWindow.set(key, r);
    }
    const reviews = [...byWindow.values()]
        .map((r) => ({ ...r, tone: reportTone(r), session: sessionsById?.get(r.sessionId) || null }))
        .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0));
    let completedAt = null;
    for (const r of reviews) if (r.completedAt != null && (completedAt == null || r.completedAt > completedAt)) completedAt = r.completedAt;
    return {
        reviews,
        superseded: adapted.length - reviews.length,
        truncated: !!truncated,
        completedAt,
        source: "pipeline",
    };
}

const severityOrder = ["low", "medium", "high", "critical"];
const sensitivityOrder = ["internal", "confidential", "restricted"];
const worstOf = (values, order) => values.reduce((w, v) => (order.indexOf(v) > order.indexOf(w) ? v : w), null);
const rankIn = (order, v) => (v ? order.indexOf(v) + 1 : 0);

// The log's summary line, as parts: a count with its label, or a bare label.
// How many rows are showing, how many flagged reviews the read found and how
// loud the worst of them is, and which bound the read hit, if any.
export function reviewsSummaryParts(list, { shown = null, sessionsCapped = false } = {}) {
    if (!list) return [];
    const n = list.reviews.length;
    const parts = [];
    if (shown != null && shown !== n) parts.push({ value: shown, label: "shown" });
    parts.push({ value: n, label: `review${n === 1 ? "" : "s"} with findings` });
    const findings = list.reviews.reduce((sum, r) => sum + (r.findingCount || 0), 0);
    if (findings) parts.push({ value: findings, label: `finding${findings === 1 ? "" : "s"}` });
    const worst = [
        worstOf(list.reviews.map((r) => r.worstBehaviour).filter(Boolean), severityOrder),
        worstOf(list.reviews.map((r) => r.worstContent).filter(Boolean), sensitivityOrder),
    ].filter(Boolean);
    for (const w of worst) parts.push({ value: null, label: w });
    if (list.truncated) parts.push({ value: null, label: `newest ${REVIEWS_LIMIT} shown` });
    if (sessionsCapped) parts.push({ value: null, label: `over the newest ${REVIEW_SESSION_CAP} sessions in scope` });
    return parts;
}

// The line under the title as one string, for a caller without the log's
// chrome (the harness, tests).
export function reviewsSummaryLine(list, opts = {}) {
    if (!list) return "";
    if (!list.reviews.length) return "no reviews with findings in this range";
    return reviewsSummaryParts(list, opts).map((p) => (p.value != null ? `${p.value} ${p.label}` : p.label)).join(" \u00b7 ");
}

// The log's search box over the list in hand: case-folded substring over the
// summary, the endpoint's name and the session id.
export function filterReviews(reviews = [], search = "", { endpointName = null } = {}) {
    const needle = String(search || "").trim().toLowerCase();
    if (!needle) return reviews;
    return reviews.filter((r) => [
        r.summary, r.sessionId, r.endpointId,
        endpointName ? endpointName(r.endpointId) : null,
        r.session?.summary,
    ].some((v) => v && String(v).toLowerCase().includes(needle)));
}

export const REVIEW_SORT_OPTIONS = [
    ["newest", "Latest review"],
    ["findings", "Most findings"],
    ["severity", "Worst first"],
    ["oldest", "Oldest first"],
];

// A client-side ordering of the rows in hand. Worst first ranks the behaviour
// rung, then the content rung, then recency, so two reviews tied on rungs keep
// the list's own order.
export function sortReviews(reviews = [], sort = "newest") {
    const byNewest = (a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0);
    const rows = [...reviews];
    if (sort === "oldest") return rows.sort((a, b) => -byNewest(a, b));
    if (sort === "findings") return rows.sort((a, b) => (b.findingCount - a.findingCount) || byNewest(a, b));
    if (sort === "severity") {
        return rows.sort((a, b) =>
            (rankIn(severityOrder, b.worstBehaviour) - rankIn(severityOrder, a.worstBehaviour))
            || (rankIn(sensitivityOrder, b.worstContent) - rankIn(sensitivityOrder, a.worstContent))
            || byNewest(a, b));
    }
    return rows.sort(byNewest);
}

// -- sessions with findings -----------------------------------------------------
// The list's grain is the SESSION: one row per conversation that has had a
// finding land on it, its flagged reviews loaded beneath. The trace log lists
// sessions by default for the same reason -- a request-grain log repeats each
// conversation once per call -- and a review-grain list repeats each session
// once per window, which on a busy tenant puts one session's reviews on
// different pages of the newest-N read.

// Sessions listed per scope, newest review first. The aggregate runs over
// every flagged report in the window, so a session's counts do not depend on
// how many rows of reports the page could hold.
export const REVIEW_SESSIONS_LIMIT = 200;
// The reports read for the listed sessions asks for exactly the entity's
// record bound; a full page leaves some sessions' older reviews unloaded, and
// those rows fall back to the aggregate's counts and say so.
export const SESSION_REPORTS_LIMIT = 1000;
// The planner refuses a typed query's offset past this, so the list stops
// offering pages there and says so; a scope with more flagged sessions than
// that is read by narrowing it.
export const SESSION_OFFSET_CEILING = 10000;

// The scope's flagged-report predicate: the sessions in scope when the scope
// is nested, and every report that kept a finding.
function flaggedFilter(sessionIds) {
    const ids = Array.isArray(sessionIds) ? uniqueIds(sessionIds, REVIEW_SESSION_CAP) : null;
    const preds = [pred(R(KEPT_FINDINGS), "GT", 0)];
    if (ids) preds.unshift(pred(R("TRACE_ID"), "IN", ids));
    return and(preds);
}

// One page of sessions, newest review first. `offset` is the count already
// listed; the total (reviewSessionsTotalSpec) says whether another page
// exists, so the page asks for exactly its size.
export function reviewSessionsSpec({ sessionIds = null, fromMs, toMs, limit = REVIEW_SESSIONS_LIMIT, offset = 0 }) {
    return {
        root: { alias: "r", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_REPORT" },
        timeRange: timeRange(fromMs, toMs),
        filter: flaggedFilter(sessionIds),
        output: {
            dimensions: [{ field: R("TRACE_ID"), outputAlias: "session_id" }],
            metrics: [
                // Windows, not publications: a re-evaluation of one window is
                // the same review twice.
                { outputAlias: "windows", computed: { field: R("TARGET_SET_HASH"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
                { outputAlias: "reports", computed: { field: R("REPORT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
                { outputAlias: "findings", computed: { field: R(KEPT_FINDINGS), op: "AGGREGATE_OP_SUM" } },
                { outputAlias: "latest", computed: { field: R("COMPLETED_AT"), op: "AGGREGATE_OP_MAX" } },
                { outputAlias: "first", computed: { field: R("COMPLETED_AT"), op: "AGGREGATE_OP_MIN" } },
            ],
        },
        orderBy: [{ outputAlias: "latest", direction: "DIRECTION_DESC" }],
        limit,
        ...(offset > 0 ? { offset } : {}),
    };
}

// How many sessions and windows with findings the scope has in the range:
// the list's true denominator, one aggregate with no grouping.
export function reviewSessionsTotalSpec({ sessionIds = null, fromMs, toMs }) {
    return {
        root: { alias: "r", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_REPORT" },
        timeRange: timeRange(fromMs, toMs),
        filter: flaggedFilter(sessionIds),
        output: {
            metrics: [
                { outputAlias: "sessions", computed: { field: R("TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
                { outputAlias: "reviews", computed: { field: R("TARGET_SET_HASH"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
            ],
        },
        limit: 1,
    };
}

// Whether the list can ask for another page: sessions remain, the last page
// was not empty, and the next offset is one the planner will accept.
export function sessionPaging({ loaded = 0, total = 0, lastPageSize = 0, ceiling = SESSION_OFFSET_CEILING } = {}) {
    const remaining = Math.max(0, num(total) - num(loaded));
    const atCeiling = num(loaded) >= ceiling;
    return { remaining, atCeiling, hasMore: remaining > 0 && num(lastPageSize) > 0 && !atCeiling };
}

export async function fetchScopeReviewTotals({ sessionIds = null, fromMs, toMs, signal = null } = {}) {
    if (SEEDED_TOTALS) return SEEDED_TOTALS;
    if (Array.isArray(sessionIds) && sessionIds.length === 0) return { sessions: 0, reviews: 0 };
    const rows = await runQueryRows(reviewSessionsTotalSpec({ sessionIds, fromMs, toMs }), { signal });
    return { sessions: num(rows[0]?.sessions), reviews: num(rows[0]?.reviews) };
}

// -> { aggregates, reports, previews, truncated, reportsTruncated }: the
// sessions with findings (newest review first), every flagged report of the
// listed sessions the page could hold, and each session's own newest prompt
// summary (the trace log's session line) for a role with sensitive read. The
// preview read is additive: a failure leaves the rows on the judge's words.
// `previews: false` leaves it out, for a reader that titles the sessions it
// shows itself.
export async function fetchScopeReviewSessions({ sessionIds = null, fromMs, toMs, sensitive = true, previews: withPreviews = true, limit = REVIEW_SESSIONS_LIMIT, offset = 0, signal = null } = {}) {
    const empty = { aggregates: [], reports: [], previews: new Map(), reportsTruncated: false };
    if (SEEDED_SESSIONS) return offset > 0 ? empty : SEEDED_SESSIONS;
    if (Array.isArray(sessionIds) && sessionIds.length === 0) return empty;
    const aggregates = await runQueryRows(reviewSessionsSpec({ sessionIds, fromMs, toMs, limit, offset }), { signal });
    const listed = uniqueIds(aggregates.map((a) => a.session_id), REVIEW_SESSION_CAP);
    if (!listed.length) return empty;
    const reports = await runQueryRows(reviewsQuerySpec({ sessionIds: listed, fromMs, toMs, limit: SESSION_REPORTS_LIMIT - 1, sensitive }), { signal });
    let previews = new Map();
    if (sensitive && withPreviews) {
        try {
            previews = await getSessionPromptPreviews(listed, timeRange(fromMs, toMs));
        } catch (error) {
            console.warn("[reviews] session summaries unavailable:", error?.message || error);
        }
    }
    return { aggregates, reports, previews, reportsTruncated: reports.length >= SESSION_REPORTS_LIMIT };
}

// Several pages as one answer: aggregates and reports in page order, previews
// merged, the reports bound reported if any page hit it.
export function mergeSessionPages(pages = []) {
    const previews = new Map();
    for (const page of pages) for (const [k, v] of page.previews || []) previews.set(k, v);
    return {
        aggregates: pages.flatMap((p) => p.aggregates || []),
        reports: pages.flatMap((p) => p.reports || []),
        previews,
        reportsTruncated: pages.some((p) => p.reportsTruncated),
    };
}

const toneRankOf = (t) => (t === "danger" ? 2 : t === "warn" ? 1 : 0);

// Aggregates, report rows and previews -> { sessions, truncated,
// reportsTruncated, superseded }. A session row carries its own newest prompt
// summary when the preview read had one (else the trace log's session row when
// the scoped read listed it, else the judge's newest summary, marked as such),
// its flagged reviews newest first, and counts: from its loaded reviews when
// the reports read held them all, else from the aggregate, which counts every
// window in the range.
export function buildSessionRows({ aggregates = [], reports = [], previews = null, sessionsById = null, reportsTruncated = false, conduct = null, nowMs = Date.now() } = {}) {
    const list = buildReviewsList(reports, { sessionsById, nowMs });
    const bySession = new Map();
    for (const r of list.reviews) {
        if (!bySession.has(r.sessionId)) bySession.set(r.sessionId, []);
        bySession.get(r.sessionId).push(r);
    }
    const sessions = (aggregates || []).map((a) => {
        const id = str(a?.session_id).toLowerCase();
        const reviews = bySession.get(id) || [];
        const preview = previews?.get(id) || null;
        const row = sessionsById?.get(id) || null;
        // Complete when the page held every one of this session's windows: the
        // page not filling means everything landed; a full page still holds a
        // session whole when its loaded windows match the aggregate's count.
        const windows = num(a?.windows);
        const complete = reviews.length > 0 && (!reportsTruncated || reviews.length >= windows);
        // The session's clock: its loaded reviews when the page held them all
        // (the aggregate's bounds include superseded publications), else the
        // aggregate's, which spans every window in the range.
        const aggLatest = parseServerTimestamp(a?.latest);
        const aggFirst = parseServerTimestamp(a?.first);
        const loadedTimes = reviews.map((r) => r.completedAt).filter((t) => t != null);
        const latestAt = complete && loadedTimes.length ? Math.max(...loadedTimes) : Number.isFinite(aggLatest) ? aggLatest : (loadedTimes.length ? Math.max(...loadedTimes) : null);
        const firstAt = complete && loadedTimes.length ? Math.min(...loadedTimes) : Number.isFinite(aggFirst) ? aggFirst : (loadedTimes.length ? Math.min(...loadedTimes) : null);
        const worstBehaviour = worstOf(reviews.map((r) => r.worstBehaviour).filter(Boolean), severityOrder);
        const worstContent = worstOf(reviews.map((r) => r.worstContent).filter(Boolean), sensitivityOrder);
        const tone = reviews.length
            ? reviews.reduce((w, r) => (toneRankOf(r.tone) > toneRankOf(w) ? r.tone : w), "calm")
            : "warn";
        const ownSummary = str(preview?.label) || str(row?.summary);
        return {
            id,
            endpointId: reviews.find((r) => r.endpointId)?.endpointId || row?.endpointId || null,
            model: str(row?.model),
            summary: ownSummary || str(reviews[0]?.summary),
            // Whose words the row's line is: the session's own prompt, or the
            // judge's newest summary standing in for it.
            summaryFrom: ownSummary ? "session" : reviews[0]?.summary ? "review" : null,
            reviews,
            reviewCount: complete ? reviews.length : windows,
            findings: complete ? reviews.reduce((n, r) => n + (r.findingCount || 0), 0) : num(a?.findings),
            complete,
            worstBehaviour,
            worstContent,
            tone,
            latestAt,
            firstAt,
            session: row,
            // The session's dominant conduct: its largest incident's type and
            // that incident's count, and every type it carries, for the chips.
            conduct: conduct?.bySession?.get(id)?.dominant || null,
            conductTypes: conduct?.bySession?.get(id)?.types || null,
        };
    }).filter((s) => s.id);
    return { sessions, reportsTruncated: !!reportsTruncated, superseded: list.superseded, conduct: conduct?.types || null, conductClipped: !!conduct?.clipped };
}

// A session's flagged reviews on its own clock, first review at the left edge
// and the latest at the right: one mark per review, toned like it.
export function sessionTimeline(session) {
    const reviews = [...(session?.reviews || [])].filter((r) => r.completedAt != null).sort((a, b) => a.completedAt - b.completedAt);
    if (!reviews.length) return { marks: [], fromMs: null, toMs: null };
    const fromMs = Math.min(session.firstAt ?? reviews[0].completedAt, reviews[0].completedAt);
    const toMs = Math.max(session.latestAt ?? reviews[reviews.length - 1].completedAt, reviews[reviews.length - 1].completedAt);
    const span = toMs - fromMs;
    return {
        fromMs,
        toMs,
        marks: reviews.map((r) => ({ id: r.id, at: r.completedAt, position: span > 0 ? (r.completedAt - fromMs) / span : 0.5, tone: r.tone, review: r })),
    };
}

// The log's search box over the session rows: the summary (the session's or
// the judge's), the endpoint's name and the session id.
export function filterSessions(sessions = [], search = "", { endpointName = null, conduct = null } = {}) {
    const needle = String(search || "").trim().toLowerCase();
    const typed = Number.isInteger(conduct) ? sessions.filter((s) => s.conductTypes?.has(conduct)) : sessions;
    if (!needle) return typed;
    return typed.filter((s) => [
        s.summary, s.id, s.endpointId, s.conduct?.name,
        endpointName ? endpointName(s.endpointId) : null,
    ].some((v) => v && String(v).toLowerCase().includes(needle)));
}

export const SESSION_SORT_OPTIONS = [
    ["latest", "Latest review"],
    ["reviews", "Most reviews"],
    ["findings", "Most findings"],
    ["conduct", "Most repeated conduct"],
    ["severity", "Worst first"],
    ["oldest", "Oldest first"],
];

export function sortSessions(sessions = [], sort = "latest") {
    const byLatest = (a, b) => (b.latestAt ?? 0) - (a.latestAt ?? 0);
    const rows = [...sessions];
    if (sort === "oldest") return rows.sort((a, b) => -byLatest(a, b));
    if (sort === "reviews") return rows.sort((a, b) => (b.reviewCount - a.reviewCount) || byLatest(a, b));
    if (sort === "findings") return rows.sort((a, b) => (b.findings - a.findings) || byLatest(a, b));
    if (sort === "conduct") return rows.sort((a, b) => ((b.conduct?.count || 0) - (a.conduct?.count || 0)) || byLatest(a, b));
    if (sort === "severity") {
        return rows.sort((a, b) =>
            (rankIn(severityOrder, b.worstBehaviour) - rankIn(severityOrder, a.worstBehaviour))
            || (rankIn(sensitivityOrder, b.worstContent) - rankIn(sensitivityOrder, a.worstContent))
            || byLatest(a, b));
    }
    return rows.sort(byLatest);
}

// The log's summary line for the session grain, as parts: the scope's true
// counts from the un-grouped aggregate, how many of its sessions the list has
// loaded when that is fewer, the loaded sessions' findings and worst rungs,
// and the bounds the reads hit.
export function sessionsSummaryParts(result, { shown = null, totals = null, sessionsCapped = false } = {}) {
    if (!result) return [];
    const loaded = result.sessions.length;
    const total = totals ? Math.max(num(totals.sessions), loaded) : loaded;
    const parts = [];
    if (shown != null && shown !== loaded) parts.push({ value: shown, label: "shown" });
    parts.push({ value: total, label: `session${total === 1 ? "" : "s"} with findings` });
    if (loaded < total) parts.push({ value: loaded, label: "loaded" });
    const reviews = totals ? num(totals.reviews) : result.sessions.reduce((sum, s) => sum + (s.reviewCount || 0), 0);
    if (reviews) parts.push({ value: reviews, label: `review${reviews === 1 ? "" : "s"}` });
    const findings = result.sessions.reduce((sum, s) => sum + (s.findings || 0), 0);
    if (findings) parts.push({ value: findings, label: `finding${findings === 1 ? "" : "s"}${loaded < total ? " in the loaded sessions" : ""}` });
    if (sessionsCapped) parts.push({ value: null, label: `over the newest ${REVIEW_SESSION_CAP} sessions in scope` });
    return parts;
}

// -- what became of the range's findings -------------------------------------
// The flow above the list starts from the report headers' own arithmetic:
// every finding a review wrote was kept, withheld, or dropped as context-only
// or unsupported. One un-grouped aggregate over every report in scope that
// wrote one; the kept findings are the ones the pool, the Triage tab and the
// list read. Counts, not prose, so any role that may list the reviews reads
// it.
const FLOW_SUMS = [
    ["written", "FINDING_COUNT"], ["kept", KEPT_FINDINGS], ["withheld", "WITHHELD_FINDING_COUNT"],
    ["context_only", "CONTEXT_ONLY_FINDING_COUNT"], ["unsupported", "UNSUPPORTED_FINDING_COUNT"],
];
export function reviewFlowSpec({ sessionIds = null, fromMs, toMs }) {
    const ids = Array.isArray(sessionIds) ? uniqueIds(sessionIds, REVIEW_SESSION_CAP) : null;
    const preds = [pred(R("FINDING_COUNT"), "GT", 0)];
    if (ids) preds.unshift(pred(R("TRACE_ID"), "IN", ids));
    return {
        root: { alias: "r", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_REPORT" },
        timeRange: timeRange(fromMs, toMs),
        filter: and(preds),
        output: {
            metrics: [
                ...FLOW_SUMS.map(([alias, name]) => ({ outputAlias: alias, computed: { field: R(name), op: "AGGREGATE_OP_SUM" } })),
                { outputAlias: "sessions", computed: { field: R("TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
            ],
        },
        limit: 1,
    };
}

// -> { written, kept, withheld, contextOnly, unsupported, sessions }.
export async function fetchReviewFlow({ sessionIds = null, fromMs, toMs, signal = null } = {}) {
    const zero = { written: 0, kept: 0, withheld: 0, contextOnly: 0, unsupported: 0, sessions: 0 };
    if (SEEDED_FLOW) return { ...zero, ...SEEDED_FLOW };
    if (Array.isArray(sessionIds) && sessionIds.length === 0) return zero;
    const rows = await runQueryRows(reviewFlowSpec({ sessionIds, fromMs, toMs }), { signal });
    const r = rows?.[0] || {};
    return {
        written: num(r.written), kept: num(r.kept), withheld: num(r.withheld),
        contextOnly: num(r.context_only), unsupported: num(r.unsupported), sessions: num(r.sessions),
    };
}

// -- an opened session's findings ---------------------------------------------

// An opened session's finding rows: which report each belongs to, how loud it
// is, what it is called and why, the passages it quoted and the messages they
// came from. Title, rationale, quotes and message ids are prose and evidence
// locations, so this read is for a role with sensitive read only.
export function listFindingsSpec({ reportIds, fromMs, toMs, limit = REVIEW_FINDINGS_LIMIT }) {
    return {
        root: { alias: "f", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING" },
        timeRange: timeRange(fromMs, toMs),
        filter: pred(F("REPORT_ID"), "IN", uniqueIds(reportIds, CITED_MESSAGE_CAP)),
        output: {
            dimensions: [
                ["ID", "finding_id"], ["REPORT_ID", "report_id"], ["COMPLETED_AT", "completed_at"], ["TITLE", "title"],
                ["RATIONALE", "rationale"],
                ["REASON", "reason"], ["BEHAVIOUR_SEVERITY", "behaviour_severity"], ["CONTENT_SENSITIVITY", "content_sensitivity"],
                ["CITATION_QUOTES", "citation_quotes"], ["CITATION_MESSAGE_IDS", "citation_message_ids"],
            ].map(([name, alias]) => ({ field: F(name), outputAlias: alias })),
        },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_DESC" }],
        limit,
    };
}

// Finding rows -> the session's findings, newest review first and in the
// read's order within a review, each with its citations as (quote, message
// id) pairs. A row of a report the session does not hold is dropped.
export function buildSessionFindings(session, rows = []) {
    const rank = new Map((session?.reviews || []).map((r, i) => [str(r.id), i]));
    const out = [];
    for (const row of rows || []) {
        const id = str(row?.finding_id);
        const reportId = str(row?.report_id);
        if (!id || !rank.has(reportId)) continue;
        out.push({
            id, reportId, session: session.id, completedAt: str(row?.completed_at),
            title: str(row?.title) || "Untitled finding",
            rationale: str(row?.rationale) || null,
            reason: str(row?.reason).toLowerCase() || null,
            severity: rung(row?.behaviour_severity, "BEHAVIOUR_SEVERITY_"),
            sensitivity: rung(row?.content_sensitivity, "CONTENT_SENSITIVITY_"),
            citations: citationsOf(row),
        });
    }
    return out
        .map((f, i) => ({ f, i }))
        .sort((a, b) => rank.get(a.f.reportId) - rank.get(b.f.reportId) || a.i - b.i)
        .map(({ f }) => f);
}

// An opened session's findings back as the pool's rows, so the triage map
// groups them the way it groups the pool's.
export function sessionFindingRows(session, findings = []) {
    return (findings || []).map((f) => ({
        finding_id: f.id, report_id: f.reportId, trace_id: session?.id || f.session, endpoint_id: session?.endpointId || "",
        completed_at: f.completedAt, title: f.title, behaviour_severity: f.severity || "", content_sensitivity: f.sensitivity || "",
        citation_quotes: (f.citations || []).map((c) => c.quote), citation_message_ids: (f.citations || []).map((c) => c.messageId || ""),
    }));
}

// -> { findings, truncated } for one opened session's reviews, in one bounded
// read. Throws on a failed read; the opened session says so.
export async function fetchSessionFindings({ session, fromMs, toMs, signal = null } = {}) {
    const reportIds = (session?.reviews || []).map((r) => str(r.id)).filter(Boolean);
    if (!reportIds.length) return { findings: [], truncated: false };
    const rows = SEEDED_SESSION_FINDINGS
        ? SEEDED_SESSION_FINDINGS.filter((r) => reportIds.includes(str(r?.report_id)))
        : await runQueryRows(listFindingsSpec({ reportIds, fromMs, toMs }), { signal });
    // A page that fills its bound may have more behind it; the entity refuses
    // a one-past read, so a full page is the truncation signal. Reports past
    // the IN-list cap were never asked for, which is truncation too.
    return {
        findings: buildSessionFindings(session, rows),
        truncated: rows.length >= REVIEW_FINDINGS_LIMIT || reportIds.length > CITED_MESSAGE_CAP,
    };
}

// -- what an opened session's reviews raised and did not keep ----------------
//
// A review keeps a finding only when its evidence holds in the turns it
// reviewed and it clears a bar for acting on it; the finding table above
// holds only those. The rest stay in the review's own record: the
// finding-detail entity, for as long as the review's document lasts (a month,
// against the header's year), with their quotes one row per citation in the
// citation entity. A review withheld a finding that cleared no bar, and
// dropped one as context-only when its quotes were found only in the earlier
// turns it was shown for context, or as unsupported when none was found.
//
// Most of what reviews drop comes from reviews that kept nothing, which the
// list never loads, so the opened session first reads its own report headers
// in the range, every review that wrote a finding, one per window as the
// list takes them.
export function sessionDropCountsSpec({ sessionId, fromMs, toMs, limit = SESSION_REPORTS_LIMIT }) {
    return {
        root: { alias: "r", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_REPORT" },
        timeRange: timeRange(fromMs, toMs),
        filter: and([pred(R("TRACE_ID"), "EQ", str(sessionId).toLowerCase()), pred(R("FINDING_COUNT"), "GT", 0)]),
        output: {
            dimensions: [
                ["REPORT_ID", "report_id"], ["TRACE_ID", "trace_id"], ["TARGET_SET_HASH", "target_set_hash"], ["SELECTION_REVISION", "selection_revision"],
                ["COMPLETED_AT", "completed_at"], ["FINDING_COUNT", "finding_count"], ["ACTIONABLE_FINDING_COUNT", "actionable_finding_count"],
            ].map(([name, alias]) => ({ field: R(name), outputAlias: alias })),
        },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_DESC" }],
        limit,
    };
}

// -> { count, reviews, truncated }: how many findings the session's reviews in
// the range wrote and did not keep, by their headers, and the reviews that
// dropped any, newest first. A page that fills its bound says `truncated`.
export async function fetchSessionDropCounts({ session, fromMs, toMs, signal = null } = {}) {
    const id = str(session?.id).toLowerCase();
    const rows = SEEDED_DROPPED
        ? SEEDED_DROPPED.reports.filter((r) => str(r?.trace_id).toLowerCase() === id)
        : await runQueryRows(sessionDropCountsSpec({ sessionId: id, fromMs, toMs }), { signal });
    const reviews = buildReviewsList(rows).reviews.filter((r) => r.writtenFindingCount > r.findingCount);
    return {
        count: reviews.reduce((n, r) => n + r.writtenFindingCount - r.findingCount, 0),
        reviews,
        truncated: (rows || []).length >= SESSION_REPORTS_LIMIT,
    };
}
const FD = (name) => ({ stepAlias: "d", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_DETAIL_${name}` });
const CT = (name) => ({ stepAlias: "c", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_CITATION_${name}` });
export const DROPPED_FINDINGS_LIMIT = 1000;
export const DROPPED_CITATIONS_LIMIT = 1000;

export function droppedFindingsSpec({ reportIds, fromMs, toMs, limit = DROPPED_FINDINGS_LIMIT }) {
    return {
        root: { alias: "d", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING_DETAIL" },
        timeRange: timeRange(fromMs, toMs),
        filter: and([pred(FD("REPORT_ID"), "IN", uniqueIds(reportIds, CITED_MESSAGE_CAP)), pred(FD("ACTIONABLE"), "EQ", false)]),
        output: {
            dimensions: [
                ["FINDING_ID", "finding_id"], ["REPORT_ID", "report_id"], ["FINDING_ORDINAL", "ordinal"], ["COMPLETED_AT", "completed_at"],
                ["TITLE", "title"], ["RATIONALE", "rationale"], ["EVIDENCE_STATUS", "evidence_status"],
                ["BEHAVIOUR_SEVERITY", "behaviour_severity"], ["CONTENT_SENSITIVITY", "content_sensitivity"],
            ].map(([name, alias]) => ({ field: FD(name), outputAlias: alias })),
        },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_DESC" }],
        limit,
    };
}

export function droppedCitationsSpec({ reportIds, fromMs, toMs, limit = DROPPED_CITATIONS_LIMIT }) {
    return {
        root: { alias: "c", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_CITATION" },
        timeRange: timeRange(fromMs, toMs),
        filter: pred(CT("REPORT_ID"), "IN", uniqueIds(reportIds, CITED_MESSAGE_CAP)),
        output: {
            dimensions: [
                ["REPORT_ID", "report_id"], ["FINDING_ORDINAL", "ordinal"], ["CITATION_ORDINAL", "citation_ordinal"],
                ["QUOTE", "quote"], ["STATUS", "status"], ["SOURCE_MESSAGE_ID", "message_id"],
            ].map(([name, alias]) => ({ field: CT(name), outputAlias: alias })),
        },
        limit,
    };
}

// Why a review did not keep a finding, from how its evidence held.
const droppedAs = (evidence) => (evidence === "context_only" || evidence === "unsupported" ? evidence : "withheld");

// Detail and citation rows -> the findings the session's reviews did not
// keep, newest review first and by ordinal within one, each with its quotes
// in order: the message a quote was found in, or none when it was not found.
export function buildDroppedFindings(session, details = [], citations = []) {
    const rank = new Map((session?.reviews || []).map((r, i) => [str(r.id).toLowerCase(), i]));
    const quotes = new Map();
    for (const c of citations || []) {
        const key = `${str(c?.report_id).toLowerCase()}|${num(c?.ordinal)}`;
        if (!quotes.has(key)) quotes.set(key, []);
        quotes.get(key).push(c);
    }
    const out = [];
    for (const row of details || []) {
        const id = str(row?.finding_id).toLowerCase();
        const reportId = str(row?.report_id).toLowerCase();
        if (!id || !rank.has(reportId)) continue;
        const ordinal = num(row?.ordinal);
        const cites = [...(quotes.get(`${reportId}|${ordinal}`) || [])].sort((a, b) => num(a?.citation_ordinal) - num(b?.citation_ordinal));
        out.push({
            id, reportId, ordinal, session: session.id, completedAt: str(row?.completed_at),
            title: str(row?.title) || "Untitled finding",
            rationale: str(row?.rationale) || null,
            severity: rung(row?.behaviour_severity, "BEHAVIOUR_SEVERITY_"),
            sensitivity: rung(row?.content_sensitivity, "CONTENT_SENSITIVITY_"),
            droppedAs: droppedAs(str(row?.evidence_status).toLowerCase()),
            citations: cites.map((c) => ({ quote: str(c?.quote), messageId: str(c?.message_id).toLowerCase() || null })),
        });
    }
    return out.sort((a, b) => rank.get(a.reportId) - rank.get(b.reportId) || a.ordinal - b.ordinal);
}

// -> { findings, expected, expired, truncated } for the reviews that dropped
// something (`session.reviews`, as fetchSessionDropCounts returns them): their
// detail rows, then the quotes of the ones that answered. `expected` is the
// headers' count and `expired` the part of it no detail row answers for, a
// review whose document has aged out; a read that fills a bound says
// `truncated` instead. Throws on a failed read.
export async function fetchSessionDroppedFindings({ session, fromMs, toMs, signal = null } = {}) {
    const expectedBy = new Map((session?.reviews || []).map((r) => [str(r.id).toLowerCase(), Math.max(0, num(r.writtenFindingCount) - num(r.findingCount))]));
    const reportIds = [...expectedBy].filter(([id, n]) => id && n > 0).map(([id]) => id);
    const expected = [...expectedBy.values()].reduce((a, b) => a + b, 0);
    if (!reportIds.length) return { findings: [], expected, expired: 0, truncated: false };
    const details = SEEDED_DROPPED
        ? SEEDED_DROPPED.details.filter((r) => reportIds.includes(str(r?.report_id).toLowerCase()))
        : await runQueryRows(droppedFindingsSpec({ reportIds, fromMs, toMs }), { signal });
    const answered = [...new Set((details || []).map((r) => str(r?.report_id).toLowerCase()).filter(Boolean))];
    const citations = !answered.length ? []
        : SEEDED_DROPPED ? SEEDED_DROPPED.citations.filter((r) => answered.includes(str(r?.report_id).toLowerCase()))
            : await runQueryRows(droppedCitationsSpec({ reportIds: answered, fromMs, toMs }), { signal });
    const findings = buildDroppedFindings(session, details, citations);
    const truncated = (details || []).length >= DROPPED_FINDINGS_LIMIT || (citations || []).length >= DROPPED_CITATIONS_LIMIT || reportIds.length > CITED_MESSAGE_CAP;
    const foundBy = new Map();
    for (const f of findings) foundBy.set(f.reportId, (foundBy.get(f.reportId) || 0) + 1);
    const expired = truncated ? 0 : reportIds.reduce((n, id) => n + Math.max(0, expectedBy.get(id) - (foundBy.get(id) || 0)), 0);
    return { findings, expected, expired, truncated };
}
